/**
 * RestaurantListingPage - Clean Redesign 2026
 * 
 * UX Principles:
 * - Mobile-first: Minimal sticky header (~100px vs ~250px before)
 * - No duplicate filters across components
 * - Clean visual hierarchy with breathing room
 * - Category discovery merged into single filter layer
 * - Reduced visual noise: fewer badges, simpler layout
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import FilterSidebar from "@/components/FilterSidebar";
import { useAuth } from "@/hooks/use-auth";
import { ArrowUp, SlidersHorizontal, X, MapPin } from "lucide-react";
import { useFilterStore } from "@/lib/FilterStore";
import { cn } from "@/lib/utils";
import { ViewMode } from "@/components/toolbar/Toolbar2026";
import { ActiveFilterChips } from "@/components/toolbar/ActiveFilterChips";
import { ResultToolbar } from "@/components/toolbar/ResultToolbar";
import { MobileFilterSheet } from "@/components/toolbar/MobileFilterSheet";
import { CardDensity } from "@/components/toolbar/DensityToggle";
import { DebugBadge } from "@/components/debug/DebugBadge";
import { Pagination } from "@/components/Pagination";
import { storage } from "@/lib/storage";

// New modular imports
import { RestaurantList } from "@/components/search/RestaurantList";
import { useRestaurantSearch } from "@/hooks/useRestaurantSearch";
import { useFilterUrlSync } from "@/hooks/useFilterUrlSync";
import { SearchInput } from "@/components/search/SearchInput";
import { LocationPicker, LocationData } from "@/components/search/LocationPicker";
import { QuickFilterChips } from "@/components/search/QuickFilterChips";
import { SortDropdown } from "@/components/search/SortDropdown";
import { ViewToggle } from "@/components/search/ViewToggle";

// New premium components
import { DeliveryModeToggle } from "@/components/search/DeliveryModeToggle";
import { CategoryDiscovery } from "@/components/search/CategoryDiscovery";
// import { PartnerCountBadge } from "@/components/search/PartnerCountBadge";
import { RestaurantMapView } from "@/components/search/RestaurantMapView";

interface AdminStatus {
  deploy?: {
    version?: string;
    commit?: string;
    buildTime?: string;
    environment?: string;
  };
}

const RestaurantListingPage = () => {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const isAdmin = !!(user && user.roles && Array.isArray(user.roles) && user.roles.some((r: string) => r.toLowerCase().includes('admin')));
  
  useFilterUrlSync();
  
  const filterStore = useFilterStore();
  const { query, location, setQuery, setLocation } = filterStore;

  const [viewMode, setViewMode] = useState<ViewMode | 'map'>(() => {
    const saved = storage.get();
    return saved.viewMode || 'list';
  });
  const [cardDensity, _setCardDensity] = useState<CardDensity>(() => {
    const saved = storage.get();
    return saved.cardDensity || 'comfort';
  });
  
  useEffect(() => {
    if (viewMode !== 'map') {
      storage.set({ viewMode: viewMode as ViewMode, cardDensity });
    }
  }, [viewMode, cardDensity]);
  
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const desktopHeaderRef = useRef<HTMLElement>(null);

  // Measure desktop header height for sticky sidebar positioning
  useEffect(() => {
    const el = desktopHeaderRef.current;
    if (!el) return;
    const updateVar = () => {
      document.documentElement.style.setProperty(
        '--desktop-header-h', 
        `${el.getBoundingClientRect().height}px`
      );
    };
    updateVar();
    const ro = new ResizeObserver(updateVar);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [showBackToTop, setShowBackToTop] = useState(false);
  
  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const {
    sortedRestaurants,
    currentRestaurants,
    cuisines,
    dietarySkills,
    currentPage,
    totalPages,
    itemsPerPage,
    setCurrentPage,
    canLoadMore,
    isLoading,
    isFetching,
    isError,
    isLoadingCuisines,
    hasUserLocation,
    refetch,
    handleResetFilters,
    sortBy: _sortBy,
    activeFilterCount
  } = useRestaurantSearch({ viewMode, cardDensity });

  const { data: _adminStatus } = useQuery<AdminStatus>({
    queryKey: ['admin', 'status', 'build'],
    queryFn: async () => {
      const response = await fetch('/api/admin/status', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch admin status');
      return response.json();
    },
    enabled: isAdmin,
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });
  
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    if (viewMode !== 'list') {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [setCurrentPage, viewMode]);

  const useLoadMore = viewMode === 'list';

  const handleLocationSelect = useCallback((loc: LocationData) => {
    if (loc.label) {
      setLocation({
        label: loc.label,
        lat: loc.lat,
        lng: loc.lng,
        source: loc.source
      });
    } else {
      setLocation(null);
    }
  }, [setLocation]);

  const handleSearchChange = useCallback((value: string) => {
    setQuery(value);
  }, [setQuery]);

  const currentLocationData: LocationData | null = location ? {
    label: location.label,
    lat: location.lat,
    lng: location.lng,
    source: (location.source as LocationData['source']) || 'manual'
  } : null;
  
  return (
    <div className="min-h-[100dvh] flex flex-col bg-[#FAFAFA]">
      <DebugBadge />
      
      {/* ============================================ */}
      {/* MOBILE HEADER - Compact 2-row layout */}
      {/* Row 1: Search + Location + Filter button */}
      {/* Row 2: DeliveryMode + Category pills (merged) */}
      {/* ============================================ */}
      <header className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        {/* Row 1: Search + Location + Filter */}
        <div 
          className="px-3 pt-2.5 pb-2 flex items-center gap-2"
          style={{
            paddingLeft: 'max(12px, env(safe-area-inset-left))',
            paddingRight: 'max(12px, env(safe-area-inset-right))'
          }}
        >
          <div className="flex-1 min-w-0">
            <SearchInput
              value={query}
              onChange={handleSearchChange}
              placeholder="Restaurant oder Gericht..."
              debounceMs={300}
              className="h-10 rounded-xl border border-gray-200/80 bg-gray-50/80 focus-within:bg-white focus-within:border-[#009544]/40 [&_input]:text-[13px] [&_input]:font-medium"
            />
          </div>
          
          <button
            onClick={() => setLocationPickerOpen(true)}
            className={cn(
              "flex-shrink-0 flex items-center gap-1 h-10 px-2.5 rounded-xl text-xs font-medium transition-colors",
              location?.label 
                ? "bg-[#009544]/8 text-[#009544]" 
                : "bg-gray-50 text-gray-400"
            )}
          >
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate max-w-[60px] hidden min-[375px]:inline">
              {location?.label ? location.label.split(',')[0] : 'Ort'}
            </span>
          </button>
          
          <button 
            onClick={() => setMobileFilterOpen(true)}
            className={cn(
              "relative flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl transition-colors",
              activeFilterCount > 0 
                ? "bg-[#009544] text-white" 
                : "bg-gray-50 text-gray-500"
            )}
            aria-label="Filter öffnen"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-white text-[9px] font-bold text-[#009544] shadow-sm">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
        
        {/* Row 2: DeliveryMode + Categories in one scrollable row */}
        <div 
          className="pb-2 px-3 flex items-center gap-1.5 overflow-x-auto scrollbar-hide"
          style={{
            paddingLeft: 'max(12px, env(safe-area-inset-left))',
            paddingRight: 'max(12px, env(safe-area-inset-right))'
          }}
        >
          <DeliveryModeToggle size="sm" className="flex-shrink-0" />
          <div className="w-px h-5 bg-gray-200 flex-shrink-0 mx-0.5" />
          <CategoryDiscovery variant="mobile" />
          <div className="w-px h-5 bg-gray-200 flex-shrink-0 mx-0.5" />
          <QuickFilterChips
            hasUserLocation={hasUserLocation}
            className="[&_button]:h-[30px] [&_button]:text-xs [&_button]:px-2.5"
          />
        </div>
        
        {/* Active filters - only when needed */}
        {activeFilterCount > 0 && (
          <div 
            className="px-3 pb-2 border-t border-gray-50"
            style={{
              paddingLeft: 'max(12px, env(safe-area-inset-left))',
              paddingRight: 'max(12px, env(safe-area-inset-right))'
            }}
          >
            <ActiveFilterChips 
              onResetAll={handleResetFilters} 
              cuisinesOverride={cuisines}
              className="flex-nowrap py-1"
            />
          </div>
        )}
      </header>
      
      {/* ============================================ */}
      {/* TABLET HEADER (md to lg) - Clean 2-row */}
      {/* ============================================ */}
      <header className="hidden md:block lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="px-6 py-3">
          <div className="flex items-center gap-3">
            <DeliveryModeToggle size="sm" className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <SearchInput
                value={query}
                onChange={handleSearchChange}
                placeholder="Restaurant, Küche oder Gericht..."
                debounceMs={300}
                className="h-11 rounded-xl bg-gray-50/80 border border-gray-200 focus-within:bg-white focus-within:border-[#009544]/40 [&_input]:text-sm"
              />
            </div>
            <button
              onClick={() => setLocationPickerOpen(true)}
              className={cn(
                "flex-shrink-0 inline-flex items-center gap-1.5 h-11 px-3 rounded-xl text-sm font-medium transition-colors",
                location?.label 
                  ? "bg-[#009544]/8 text-[#009544]" 
                  : "bg-gray-50 text-gray-500"
              )}
            >
              <MapPin className="w-4 h-4" />
              <span className="truncate max-w-[120px]">
                {location?.label ? location.label.split(',')[0] : 'Standort'}
              </span>
            </button>
            <button 
              onClick={() => setMobileFilterOpen(true)}
              className={cn(
                "relative flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl transition-colors",
                activeFilterCount > 0 
                  ? "bg-[#009544] text-white" 
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              )}
              aria-label="Filter öffnen"
            >
              <SlidersHorizontal className="w-5 h-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-white text-[10px] font-bold text-[#009544] shadow-sm">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
        
        <div className="pb-2.5 px-6">
          <CategoryDiscovery variant="desktop" />
        </div>
        
        {activeFilterCount > 0 && (
          <div className="px-6 pb-2 border-t border-gray-50">
            <ActiveFilterChips 
              onResetAll={handleResetFilters} 
              cuisinesOverride={cuisines}
              className="flex-nowrap py-1"
            />
          </div>
        )}
      </header>

      {/* ============================================ */}
      {/* DESKTOP HEADER - Single row + category strip */}
      {/* ============================================ */}
      <header ref={desktopHeaderRef} className="hidden lg:block sticky top-0 z-40 bg-white/98 backdrop-blur-md border-b border-gray-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="max-w-[1440px] mx-auto px-6 xl:px-8">
          {/* Row 1: All controls */}
          <div className="flex items-center gap-4 h-[60px]">
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <DeliveryModeToggle size="md" className="flex-shrink-0" />
              <button
                onClick={() => setLocationPickerOpen(true)}
                className={cn(
                  "inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl text-sm font-medium transition-colors",
                  location?.label 
                    ? "bg-[#009544]/8 text-[#009544] hover:bg-[#009544]/12" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-150"
                )}
              >
                <MapPin className="w-4 h-4" />
                <span className="truncate max-w-[140px]">
                  {location?.label || 'Standort wählen'}
                </span>
                {location?.label && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setLocation(null); }}
                    className="ml-0.5 p-0.5 rounded hover:bg-[#009544]/15"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            </div>
            
            <div className="flex-1 min-w-0 max-w-xl">
              <SearchInput
                value={query}
                onChange={handleSearchChange}
                placeholder="Restaurant, Küche oder Gericht..."
                debounceMs={300}
                className="h-10 bg-gray-50 border border-gray-200 focus-within:bg-white focus-within:border-[#009544]/40 [&_input]:text-sm"
              />
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <SortDropdown hasLocation={hasUserLocation} className="h-10 text-sm" />
              <ViewToggle 
                value={viewMode === 'map' ? 'map' : viewMode} 
                onChange={(v) => setViewMode(v)}
                size="sm"
                mapOnly
                className="h-10 w-10"
              />
            </div>
          </div>
          
          {/* Row 2: Categories + Quick filters */}
          <div className="flex items-center gap-4 pb-2.5 -mt-0.5">
            <div className="flex-1 min-w-0 overflow-hidden">
              <CategoryDiscovery variant="desktop" />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pl-4 border-l border-gray-200/60">
              <QuickFilterChips 
                hasUserLocation={hasUserLocation}
                className="[&_button]:h-8 [&_button]:text-xs [&_button]:px-3"
              />
            </div>
          </div>
        </div>
      </header>

      {/* ============================================ */}
      {/* MAIN CONTENT */}
      {/* ============================================ */}
      <main className="flex-1">
        <div 
          className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-6 xl:px-8 py-4 lg:py-5"
          style={{ 
            paddingLeft: 'max(16px, env(safe-area-inset-left))',
            paddingRight: 'max(16px, env(safe-area-inset-right))'
          }}
        >
          <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-6">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block">
              <div 
                className="sticky overflow-y-auto rounded-xl"
                style={{ 
                  top: 'calc(var(--desktop-header-h, 110px) + 16px)',
                  maxHeight: 'calc(100vh - var(--desktop-header-h, 110px) - 32px)'
                }}
              >
                <FilterSidebar cuisinesOverride={cuisines} isLoadingCuisinesOverride={isLoadingCuisines} />
              </div>
            </aside>
            
            {/* Results */}
            <section className="min-w-0">
              {/* Desktop */}
              <div className="hidden lg:block">
                {/* Info bar */}
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-gray-900">{sortedRestaurants.length}</span>
                    <span className="text-sm text-gray-400">
                      Restaurant{sortedRestaurants.length !== 1 ? 's' : ''}
                    </span>
                    {location?.label && (
                      <span className="text-sm text-gray-400 hidden xl:inline">
                        in <span className="font-medium text-gray-500">{location.label}</span>
                      </span>
                    )}
                  </div>
                  
                  {activeFilterCount > 0 && (
                    <ResultToolbar 
                      onResetAll={handleResetFilters} 
                      cuisinesOverride={cuisines}
                      hideDeliveryMode={true}
                      hideLocation={true}
                    />
                  )}
                </div>
                
                <div>
                  <div ref={resultsRef}>
                  {viewMode === 'map' ? (
                    <RestaurantMapView
                      restaurants={sortedRestaurants}
                      userLocation={hasUserLocation && location?.lat && location?.lng 
                        ? { lat: location.lat, lng: location.lng } 
                        : null
                      }
                      onRestaurantClick={(restaurant) => {
                        navigate(`/restaurants/${restaurant.slug || restaurant.id}`);
                      }}
                      onBackToList={() => setViewMode('list')}
                      className="h-[600px] rounded-xl overflow-hidden"
                    />
                  ) : (
                    <>
                      <RestaurantList
                        restaurants={currentRestaurants}
                        viewMode={viewMode}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        isError={isError}
                        itemsPerPage={itemsPerPage}
                        totalPages={totalPages}
                        currentPage={currentPage}
                        canLoadMore={canLoadMore}
                        onRetry={refetch}
                        onResetFilters={handleResetFilters}
                        onPageChange={handlePageChange}
                        onChangeLocation={() => setLocationPickerOpen(true)}
                      />
                      
                      {!isError && !isLoading && totalPages > 1 && !useLoadMore && (
                        <div className="mt-8 pt-6 border-t border-gray-100">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                          />
                        </div>
                      )}
                    </>
                  )}
                  </div>
                </div>
              </div>
              
              {/* Mobile */}
              <div className="lg:hidden">
                {/* Mini toolbar */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{sortedRestaurants.length}</span>
                    <span className="text-xs text-gray-400">Restaurants</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SortDropdown 
                      hasLocation={hasUserLocation} 
                      className="h-8 text-xs"
                    />
                    <ViewToggle 
                      value={viewMode === 'map' ? 'map' : viewMode} 
                      onChange={(v) => setViewMode(v)}
                      size="sm"
                      mapOnly
                      className="h-8 w-8"
                    />
                  </div>
                </div>
                
                <div ref={resultsRef}>
                  {viewMode === 'map' ? (
                    <RestaurantMapView
                      restaurants={sortedRestaurants}
                      userLocation={hasUserLocation && location?.lat && location?.lng 
                        ? { lat: location.lat, lng: location.lng } 
                        : null
                      }
                      onRestaurantClick={(restaurant) => {
                        navigate(`/restaurants/${restaurant.slug || restaurant.id}`);
                      }}
                      onBackToList={() => setViewMode('list')}
                      className="h-[calc(100vh-200px)] min-h-[400px] rounded-xl overflow-hidden"
                    />
                  ) : (
                    <>
                      <RestaurantList
                        restaurants={currentRestaurants}
                        viewMode={viewMode as ViewMode}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        isError={isError}
                        itemsPerPage={itemsPerPage}
                        totalPages={totalPages}
                        currentPage={currentPage}
                        canLoadMore={canLoadMore}
                        onRetry={refetch}
                        onResetFilters={handleResetFilters}
                        onPageChange={handlePageChange}
                        onChangeLocation={() => setLocationPickerOpen(true)}
                      />
                      
                      {!isError && !isLoading && totalPages > 1 && !useLoadMore && (
                        <div className="mt-8 pb-8">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* ============================================ */}
      {/* MODALS & OVERLAYS */}
      {/* ============================================ */}
      
      <LocationPicker
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        currentLocation={currentLocationData}
        onLocationSelect={handleLocationSelect}
        restaurants={sortedRestaurants.filter(r => r.latitude && r.longitude).map(r => ({
          id: r.id,
          name: r.name,
          lat: Number(r.latitude),
          lng: Number(r.longitude)
        }))}
      />
      
      <MobileFilterSheet
        open={mobileFilterOpen}
        onOpenChange={setMobileFilterOpen}
        restaurantCount={sortedRestaurants.length}
        onResetFilters={handleResetFilters}
        cuisines={cuisines}
        dietarySkills={dietarySkills}
      />
      
      {/* Back-to-Top */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={cn(
          "fixed bottom-5 right-5 z-50",
          "w-11 h-11 rounded-full",
          "bg-white text-gray-600 shadow-lg border border-gray-200/80",
          "hover:shadow-xl hover:text-[#009544]",
          "transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-[#009544] focus:ring-offset-2",
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 pointer-events-none translate-y-4"
        )}
        aria-label="Zurück nach oben"
      >
        <ArrowUp className="h-4.5 w-4.5 mx-auto" />
      </button>
    </div>
  );
};

export default RestaurantListingPage;
